import { useEffect } from 'react';

export type SEOProps = {
  title: string;
  description?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article';
};

const ensureTag = (selector: string, create: () => HTMLElement) => {
  let el = document.head.querySelector(selector) as HTMLElement | null;
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  return el as HTMLElement;
};

const absoluteUrl = (path?: string) => {
  if (!path) return undefined;
  if (/^https?:\/\//i.test(path)) return path;
  const origin = window.location.origin;
  return origin + (path.startsWith('/') ? path : `/${path}`);
};

const SEO = ({ title, description, image, url, type = 'website' }: SEOProps) => {
  useEffect(() => {
    // Title - update both document title and existing meta tag
    document.title = title;
    const titleElement = document.getElementById('page-title');
    if (titleElement) titleElement.textContent = title;

    // Description - update existing meta tag
    if (description) {
      const existingDesc = document.getElementById('page-description') as HTMLMetaElement;
      if (existingDesc) {
        existingDesc.content = description;
      } else {
        const metaDesc = ensureTag('meta[name="description"]', () => {
          const m = document.createElement('meta');
          m.setAttribute('name', 'description');
          return m;
        }) as HTMLMetaElement;
        metaDesc.content = description;
      }
    }

    const canonicalHref = url || window.location.href;
    const ogUrl = canonicalHref;
    const ogImage = absoluteUrl(image);

    // Canonical
    const linkCanonical = ensureTag('link[rel="canonical"]', () => {
      const l = document.createElement('link');
      l.setAttribute('rel', 'canonical');
      return l;
    }) as HTMLLinkElement;
    linkCanonical.href = canonicalHref;

    // Open Graph - update existing tags first, then create new ones if needed
    const setOg = (prop: string, content: string | undefined, id?: string) => {
      if (!content) return;
      
      // Remove any existing meta tags with this property first
      const existingMetas = document.querySelectorAll(`meta[property="${prop}"]`);
      existingMetas.forEach(meta => {
        if (!id || meta.id !== id) {
          meta.remove();
        }
      });
      
      const existingElement = id ? document.getElementById(id) as HTMLMetaElement : null;
      if (existingElement) {
        existingElement.content = content;
      } else {
        const meta = document.createElement('meta');
        meta.setAttribute('property', prop);
        meta.content = content;
        if (id) meta.id = id;
        document.head.appendChild(meta);
      }
    };

    setOg('og:title', title, 'og-title');
    setOg('og:description', description, 'og-description');
    setOg('og:type', type);
    setOg('og:url', ogUrl);
    if (ogImage) setOg('og:image', ogImage);

    // Twitter - ensure these are properly set for social sharing
    const setTw = (name: string, content: string | undefined, id?: string) => {
      if (!content) return;
      
      // Remove any existing meta tags with this name first
      const existingMetas = document.querySelectorAll(`meta[name="${name}"]`);
      existingMetas.forEach(meta => {
        if (!id || meta.id !== id) {
          meta.remove();
        }
      });
      
      const existingElement = id ? document.getElementById(id) as HTMLMetaElement : null;
      if (existingElement) {
        existingElement.content = content;
      } else {
        const meta = document.createElement('meta');
        meta.setAttribute('name', name);
        meta.content = content;
        if (id) meta.id = id;
        document.head.appendChild(meta);
      }
    };
    setTw('twitter:card', 'summary_large_image');
    setTw('twitter:title', title);
    setTw('twitter:description', description);
    if (ogImage) setTw('twitter:image', ogImage);

    // Force refresh of social media meta tags for better sharing
    const metaRefresh = ensureTag('meta[name="robots"]', () => {
      const m = document.createElement('meta');
      m.setAttribute('name', 'robots');
      return m;
    }) as HTMLMetaElement;
    metaRefresh.content = 'index, follow';
  }, [title, description, image, url, type]);

  return null;
};

export default SEO;
